<script setup lang="ts">
import { computed, ref, watch } from "vue";
import Modal from "./Modal.vue";
import Button from "./Button.vue";
import Select from "./Select.vue";
import StatusChip from "./StatusChip.vue";
import Icon from "./Icon.vue";
import type { Outcome } from "../api/types";
import { localized, t } from "../i18n";
const props = defineProps<{
  open: boolean;
  outcomes: Outcome[];
  busy?: boolean;
  suggestion?: { code: string; confidence: number; reason: string } | null;
}>();
const emit = defineEmits<{
  close: [];
  confirm: [
    payload: { outcomeCode: string; note?: string; ifNoReplyHours?: number },
  ];
}>();
const choice = ref("");
const note = ref("");
/** Now, or only if the contact does not answer within these hours ("concluir se não responder"). */
const WAITS = [0, 1, 2, 4, 8, 12, 24, 48, 72, 168];
const wait = ref(0);
const waitLabel = (h: number) =>
  h === 0
    ? t("outcome.when.now")
    : h % 24 === 0
      ? h === 24
        ? t("outcome.when.day")
        : t("outcome.when.days", { n: h / 24 })
      : h === 1
        ? t("outcome.when.hour")
        : t("outcome.when.hours", { n: h });
const waitModel = computed({
  get: () => waitLabel(wait.value),
  set: (label: string) => {
    wait.value = WAITS.find((h) => waitLabel(h) === label) ?? 0;
  },
});
const labels = computed(() => props.outcomes.map((o) => localized(o.label)));
const selected = computed(() =>
  props.outcomes.find((o) => localized(o.label) === choice.value),
);
const needsNote = computed(
  () => Boolean(selected.value?.requires_note) && !note.value.trim(),
);
const suggested = computed(() =>
  props.suggestion
    ? props.outcomes.find((o) => o.code === props.suggestion!.code)
    : undefined,
);
watch(
  () => props.open,
  (v) => {
    if (v) {
      choice.value =
        (suggested.value && localized(suggested.value.label)) ||
        labels.value[0] ||
        "";
      note.value = "";
      wait.value = 0;
    }
  },
);
// The suggestion may arrive after the modal opened: preselect it once if the user has not chosen yet.
watch(suggested, (o) => {
  if (o && props.open && choice.value === (labels.value[0] ?? ""))
    choice.value = localized(o.label);
});
function confirm() {
  if (!selected.value || needsNote.value) return;
  emit("confirm", {
    outcomeCode: selected.value.code,
    ...(note.value.trim() ? { note: note.value.trim() } : {}),
    ...(wait.value ? { ifNoReplyHours: wait.value } : {}),
  });
}
</script>
<template>
  <Modal :open="open" :title="t('outcome.title')" @close="emit('close')">
    <div class="modal-form">
      <div v-if="suggested && suggestion" class="outcome-suggestion">
        <Icon name="sparkles" :size="16" />
        <span class="grow"
          >{{
            t("outcome.suggestion", {
              label: localized(suggested.label),
              confidence: Math.round(suggestion.confidence * 100),
            })
          }}<small v-if="suggestion.reason" class="muted">
            · {{ suggestion.reason }}</small
          ></span
        >
      </div>
      <Select v-model="choice" :label="t('outcome.select')" :options="labels" />
      <div v-if="selected" class="row" style="gap: 8px">
        <StatusChip
          :status="
            selected.is_success
              ? 'success'
              : selected.category === 'negative' ||
                  selected.category === 'invalid'
                ? 'failed'
                : 'closed'
          "
          :label="t(('outcome.' + selected.category) as never)"
        />
      </div>
      <label class="wa-field"
        ><span>{{ t("outcome.note") }}</span
        ><textarea placeholder=" " v-model="note" rows="3" />
      </label>
      <p v-if="needsNote" class="small danger">
        {{ t("outcome.note_required") }}
      </p>
      <Select
        v-model="waitModel"
        :label="t('outcome.when_label')"
        :options="WAITS.map(waitLabel)"
      />
      <p v-if="wait" class="small muted">{{ t("outcome.when_hint") }}</p>
    </div>
    <template #footer>
      <Button variant="secondary" @click="emit('close')">{{
        t("common.cancel")
      }}</Button>
      <Button
        :loading="busy"
        :disabled="!selected || needsNote"
        @click="confirm"
        >{{ wait ? t("outcome.schedule") : t("outcome.confirm") }}</Button
      >
    </template>
  </Modal>
</template>
